/**
 * IMPORTS
 */

import { useEffect, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

// hooks / zustand
import { useStoreZustandPegarTarefas } from "@/store-zustand/admin/pegar-tarefas";
import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

// domain
import { handleGetPegarTarefas } from "@/domain/use-cases/pick-up-task";
import { getStatusLabel } from "@/utils/get-status-label";

export interface ITarefas {
  id: number;
  user_id: number;
  modelo_tecnico_id: number;
  tecnologia_id: number;
  total_solicitacoes: number;
  situacao: string;
  data_remessa: string;
  data_inicio_producao: string;
  posicao: string;
  observacao: string;
  prazo: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  cliente: any;
  tecnologia: any;
  modelo_tecnico: any;
  solicitante: any;
  designer: any;
  consultor?: any; // opcional, você usa no map
  responsavel?: string; // idem
  status: string;
  numero_remessa: string;
}

export interface IPagination {
  current_page: number;
  per_page: number;
  // API Nest: total da lista (já filtrado pela busca) e última página
  total?: number;
  last_page?: number;
  has_more_pages: boolean;
  next_page_url: string | null;
  previous_page_url: string | null;
}

export interface ITarefasResponse {
  code: number;
  status: string;
  message: string;
  data: ITarefas[];
  pagination: IPagination;
  total_remessas_solicitadas?: number;
  total_remessas_em_producao?: number;
}

function derivePaginationMeta(remessaResponse: ITarefasResponse | undefined, itemsPerPage: number) {
  const pagination = remessaResponse?.pagination;
  const perPage = pagination?.per_page ?? itemsPerPage;
  // A API Nest manda o total em pagination.total; o campo total_remessas_solicitadas
  // só vinha do Laravel e fica de fallback.
  const total = pagination?.total ?? remessaResponse?.total_remessas_solicitadas ?? 0;

  if (total > 0) {
    return {
      total,
      lastPage: pagination?.last_page ?? Math.max(1, Math.ceil(total / perPage)),
    };
  }

  const currentPage = pagination?.current_page ?? 1;
  const hasMorePages = pagination?.has_more_pages ?? false;

  return {
    total: 0,
    lastPage: hasMorePages ? currentPage + 1 : currentPage,
  };
}

function mapRemessasToTable(data: ITarefas[]): Record<string, any>[] {
  return data?.map((remessa) => ({
    remessa: remessa.id,
    numero_remessa: remessa.numero_remessa ?? "N/A",
    nome_identificacao: remessa.cliente?.nome ?? "N/A",
    quantidadeSolicitacao: remessa.total_solicitacoes ?? 0,
    status: getStatusLabel(remessa.situacao) ?? "N/A",
    data: remessa.created_at ? new Date(remessa.created_at).toLocaleString("pt-BR") : "N/A",
    prazo: `${remessa.prazo > 1 ? `${remessa.prazo} dias` : `${remessa.prazo} dia`}`,
    posicao: remessa.posicao ?? "N/A",
    tecnologia: remessa.tecnologia?.nome ?? "N/A",
    modelo: remessa.modelo_tecnico?.nome_modelo ?? "N/A",
    modelo_id: remessa.modelo_tecnico?.id ?? null,
    solicitante: remessa.solicitante?.nome ?? "N/A",
    designer: remessa?.designer?.nome ?? "N/A",
    situacao: remessa.situacao ?? "N/A",
    observacao: remessa?.observacao || "",
  }));
}

export function generatePagination(
  currentPage: number,
  lastPage: number,
  delta = 2
): (number | string)[] {
  const range: (number | string)[] = [];
  const left = Math.max(2, currentPage - delta);
  const right = Math.min(lastPage - 1, currentPage + delta);

  range.push(1);

  if (left > 2) {
    range.push("...");
  }

  for (let i = left; i <= right; i++) {
    range.push(i);
  }

  if (right < lastPage - 1) {
    range.push("...");
  }

  if (lastPage > 1) {
    range.push(lastPage);
  }

  return range;
}

function usePegarTarefasAdmin() {
  const { currentPage, itemsPerPage, searchItem, setPaginations, setLastPage, setTotalItemsPage } =
    useStoreZustandPegarTarefas();

  const { token: accessToken } = useStoreZustandUserAuth();

  const queryKey = ["pega-tarefas-admin", currentPage, itemsPerPage, searchItem];

  const {
    isLoading,
    error: isError,
    data: remessaResponse,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const responsePegasTarefasAdmin = await handleGetPegarTarefas(
        accessToken!,
        currentPage,
        itemsPerPage,
        searchItem!
      );
      return responsePegasTarefasAdmin;
    },
  });

  const tarefas = mapRemessasToTable(remessaResponse?.data);

  const { total, lastPage } = derivePaginationMeta(remessaResponse, itemsPerPage);

  const pages = useMemo(() => {
    return generatePagination(currentPage, lastPage);
  }, [currentPage, lastPage]);

  useEffect(() => {
    setPaginations(pages as number[]);
    setLastPage(lastPage);
    setTotalItemsPage(total);
  }, [pages, setPaginations, setLastPage, lastPage, total, setTotalItemsPage]);

  return {
    downloads: tarefas ?? [],
    paginations: pages,
    total_remessas_solicitadas: total,
    isLoading,
    isError,
  };
}

export { usePegarTarefasAdmin };
